import React, { useState } from 'react';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import Icon from '../../../components/common/Icon/Icon';
import ImageSourcePickerModal from '../../../components/common/ImageSourcePickerModal/ImageSourcePickerModal';
import PhotoFullscreenModal from '../../../components/common/PhotoFullscreenModal/PhotoFullscreenModal';
import { createLocalMealPhoto } from '../../../contexts/MealTrackingContext';
import type { MealPhoto } from '../../../types/mealTracking';
import { ACCEPTED_MEAL_PHOTO_TYPES, MAX_MEAL_PHOTOS } from '../mealTracking.constants';
import styles from './MealPhotoUploadField.module.css';

interface MealPhotoUploadFieldProps {
  photos: MealPhoto[];
  onChange: (photos: MealPhoto[]) => void;
  disabled?: boolean;
}

const isAcceptedMealPhoto = (file: File): boolean => (
  ACCEPTED_MEAL_PHOTO_TYPES.includes(file.type.toLowerCase())
);

const MealPhotoUploadField: React.FC<MealPhotoUploadFieldProps> = ({
  photos,
  onChange,
  disabled = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState<MealPhoto | null>(null);
  const [pendingRemovePhotoId, setPendingRemovePhotoId] = useState<string | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);

  const canAddMore = photos.length < MAX_MEAL_PHOTOS;

  const handleFilesSelected = (files: File[]) => {
    const accepted = files.filter(isAcceptedMealPhoto);
    if (accepted.length === 0) {
      window.alert('Envie uma imagem em JPG, PNG ou WebP.');
      return;
    }

    if (replaceTargetId) {
      const replacement = createLocalMealPhoto(accepted[0]);
      onChange(photos.map((photo) => {
        if (photo.id !== replaceTargetId) {
          return photo;
        }

        if (!photo.isDemoAsset && photo.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(photo.previewUrl);
        }

        return replacement;
      }));
      setReplaceTargetId(null);
      return;
    }

    const remainingSlots = MAX_MEAL_PHOTOS - photos.length;
    const nextPhotos = accepted.slice(0, remainingSlots).map(createLocalMealPhoto);
    onChange([...photos, ...nextPhotos]);
  };

  const removePhoto = (photoId: string) => {
    const target = photos.find((photo) => photo.id === photoId);
    if (target && !target.isDemoAsset && target.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(target.previewUrl);
    }

    onChange(photos.filter((photo) => photo.id !== photoId));
    setPendingRemovePhotoId(null);
  };

  return (
    <div className={styles.field}>
      <button
        type="button"
        className={styles.dropzone}
        disabled={disabled || !canAddMore}
        onClick={() => setPickerOpen(true)}
      >
        <Icon name="photo_camera" size={22} color="var(--color-text-muted)" />
        <span className={styles.dropzoneCopy}>
          <span className={styles.dropzoneTitle}>Adicionar foto</span>
          <span className={styles.dropzoneHint}>JPG, PNG ou WebP · até {MAX_MEAL_PHOTOS} fotos</span>
        </span>
        <span className={styles.dropzoneAction}>Selecionar</span>
      </button>

      {photos.length > 0 && (
        <div className={styles.thumbnailGrid}>
          {photos.map((photo) => (
            <div key={photo.id} className={styles.thumbnailCard}>
              <button
                type="button"
                className={styles.thumbnailPreview}
                onClick={() => setFullscreenPhoto(photo)}
                aria-label={`Visualizar ${photo.fileName}`}
              >
                <img src={photo.previewUrl} alt={photo.fileName} className={styles.thumbnailImage} />
              </button>
              <div className={styles.thumbnailActions}>
                <button
                  type="button"
                  className={styles.textAction}
                  onClick={() => {
                    setReplaceTargetId(photo.id);
                    setPickerOpen(true);
                  }}
                  disabled={disabled}
                >
                  Substituir
                </button>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => setPendingRemovePhotoId(photo.id)}
                  aria-label="Remover foto"
                  disabled={disabled}
                >
                  <Icon name="cancel" size={16} color="var(--color-error)" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ImageSourcePickerModal
        open={pickerOpen}
        title="Adicionar foto da refeição"
        galleryMultiple={!replaceTargetId && canAddMore && photos.length < MAX_MEAL_PHOTOS - 1}
        onCancel={() => {
          setReplaceTargetId(null);
          setPickerOpen(false);
        }}
        onFilesSelected={(files) => {
          setPickerOpen(false);
          handleFilesSelected(files);
        }}
      />

      <PhotoFullscreenModal
        open={Boolean(fullscreenPhoto)}
        src={fullscreenPhoto?.previewUrl ?? ''}
        alt={fullscreenPhoto?.fileName}
        onClose={() => setFullscreenPhoto(null)}
      />

      <ConfirmModal
        open={Boolean(pendingRemovePhotoId)}
        title="Remover foto"
        message="Deseja remover esta foto da refeição?"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingRemovePhotoId(null)}
        onConfirm={() => {
          if (pendingRemovePhotoId) {
            removePhoto(pendingRemovePhotoId);
          }
        }}
      />
    </div>
  );
};

export default MealPhotoUploadField;
